// Circle CCTP V2, as the gateway and the keeper use it: the message byte layout (MessageV2 header, BurnMessageV2
// body, from circlefin/evm-cctp-contracts), Circle's attestation API (Iris) and the choice of the burn to finalize.
// Pure viem, no Node imports: the frontend shares it. Layout and addresses checked onchain on 2026-10-16.
import { bytesToHex, concatHex, getAddress, hexToBytes, numberToHex, pad, type Address, type Hex } from 'viem';

/** HyperEVM's CCTP domain, mainnet and testnet alike */
export const HYPEREVM_DOMAIN = 19;

/** CCTP domains of the chains Circle lists, for display; mainnet and testnet share the ids */
export const DOMAIN_NAMES: Record<number, string> = {
  0: 'Ethereum',
  1: 'Avalanche',
  2: 'OP Mainnet',
  3: 'Arbitrum',
  6: 'Base',
  7: 'Polygon',
  10: 'Unichain',
  11: 'Linea',
  12: 'Codex',
  13: 'Sonic',
  14: 'World Chain',
  15: 'Monad',
  16: 'Sei',
  18: 'XDC',
  19: 'HyperEVM',
  21: 'Ink',
  22: 'Plume',
  26: 'Arc',
};
export const domainName = (domain: number): string => DOMAIN_NAMES[domain] ?? `domain ${domain}`;

/** Circle's attestation API; the sandbox serves the testnets */
export const IRIS_URL = { mainnet: 'https://iris-api.circle.com', testnet: 'https://iris-api-sandbox.circle.com' } as const;
export const irisUrlFor = (chainId: number): string => (chainId === 998 ? IRIS_URL.testnet : IRIS_URL.mainnet);

/**
 * `minFinalityThreshold` of a burn: 1000 lets Circle attest once the source block is confirmed (a fast transfer,
 * with a fee in bps of the amount), 2000 only once it is final (a standard transfer, no fee, up to ~20 min from
 * Ethereum and the L2s that settle on it)
 */
export const FINALITY = { fast: 1000, standard: 2000 } as const;

const HEADER = 148;
const BODY = 228;
/** A V2 burn message is at least this long: the header, then the body's fixed fields; the hook data follows */
export const MIN_MESSAGE_BYTES = HEADER + BODY;

/** A V2 burn message, every 32-byte field kept as its word (addresses are left-padded in them) */
export type CctpMessage = {
  version: number;
  sourceDomain: number;
  destinationDomain: number;
  nonce: Hex;
  sender: Hex;
  recipient: Hex;
  destinationCaller: Hex;
  minFinalityThreshold: number;
  finalityThresholdExecuted: number;
  bodyVersion: number;
  burnToken: Hex;
  mintRecipient: Hex;
  amount: bigint;
  messageSender: Hex;
  maxFee: bigint;
  feeExecuted: bigint;
  expirationBlock: bigint;
  hookData: Hex;
};

/** An address as the left-padded, lowercase word the messages carry */
export const addressToWord = (a: Address): Hex => pad(a.toLowerCase() as Hex, { size: 32 });

/** The address a left-padded word holds; undefined for zero or anything with higher bits set */
export function wordToAddress(word: Hex): Address | undefined {
  const v = BigInt(word);
  if (v === 0n || v >> 160n !== 0n) return undefined;
  return getAddress(`0x${v.toString(16).padStart(40, '0')}`);
}

export function encodeCctpMessage(m: CctpMessage): Hex {
  const u32 = (v: number) => numberToHex(v, { size: 4 });
  const u256 = (v: bigint) => numberToHex(v, { size: 32 });
  const body = concatHex([
    u32(m.bodyVersion),
    m.burnToken,
    m.mintRecipient,
    u256(m.amount),
    m.messageSender,
    u256(m.maxFee),
    u256(m.feeExecuted),
    u256(m.expirationBlock),
    m.hookData,
  ]);
  return concatHex([
    u32(m.version),
    u32(m.sourceDomain),
    u32(m.destinationDomain),
    m.nonce,
    m.sender,
    m.recipient,
    m.destinationCaller,
    u32(m.minFinalityThreshold),
    u32(m.finalityThresholdExecuted),
    body,
  ]);
}

export function decodeCctpMessage(message: Hex): CctpMessage {
  const b = hexToBytes(message);
  if (b.length < MIN_MESSAGE_BYTES) throw new Error(`CCTP message too short: ${b.length} bytes`);
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const u32 = (i: number) => dv.getUint32(i);
  const word = (i: number): Hex => bytesToHex(b.subarray(i, i + 32));
  const u256 = (i: number) => BigInt(word(i));
  return {
    version: u32(0),
    sourceDomain: u32(4),
    destinationDomain: u32(8),
    nonce: word(12),
    sender: word(44),
    recipient: word(76),
    destinationCaller: word(108),
    minFinalityThreshold: u32(140),
    finalityThresholdExecuted: u32(144),
    bodyVersion: u32(HEADER),
    burnToken: word(HEADER + 4),
    mintRecipient: word(HEADER + 36),
    amount: u256(HEADER + 68),
    messageSender: word(HEADER + 100),
    maxFee: u256(HEADER + 132),
    feeExecuted: u256(HEADER + 164),
    expirationBlock: u256(HEADER + 196),
    hookData: b.length > HEADER + BODY ? bytesToHex(b.subarray(HEADER + BODY)) : '0x',
  };
}

/** Who the gateway credits: the first word of the hook data when it is an address, otherwise the burn's sender */
export function beneficiary(m: CctpMessage): Address | undefined {
  const hook = m.hookData.length >= 66 ? wordToAddress(`0x${m.hookData.slice(2, 66)}`) : undefined;
  return hook ?? wordToAddress(m.messageSender);
}

/**
 * `maxFee` for a burn: Circle's quoted bps rounded up, with a tenth more so a quote that moves between the
 * request and the burn does not stall it (only the fee Circle executes is charged, never the cap)
 */
export function maxFeeFor(amount: bigint, bps: number): bigint {
  if (bps <= 0) return 0n;
  const ppm = BigInt(Math.ceil(bps * 100 * 1.1)); // bps -> parts per million, plus 10%
  return (amount * ppm + 999_999n) / 1_000_000n;
}

// ---------------------------------------------------------------- Iris

export type IrisMessage = {
  /** '0x' until attested */
  message: Hex;
  eventNonce: string;
  attestation: Hex | 'PENDING' | null;
  cctpVersion: 1 | 2;
  status: 'complete' | 'pending_confirmations';
  delayReason: string | null;
};

export type IrisLookup = { status: number; messages: IrisMessage[] };

/** The messages of a burn transaction; 404 (not indexed yet, or no burn in it) comes back as an empty list */
export async function irisMessages(base: string, sourceDomain: number, txHash: Hex, fetchFn: typeof fetch = fetch): Promise<IrisLookup> {
  const res = await fetchFn(`${base}/v2/messages/${sourceDomain}?transactionHash=${txHash}`, { signal: AbortSignal.timeout(10_000) });
  if (res.status === 404) return { status: 404, messages: [] };
  if (!res.ok) throw new Error(`Iris HTTP ${res.status}`);
  const body = (await res.json()) as { messages?: IrisMessage[] };
  return { status: res.status, messages: body.messages ?? [] };
}

/** Asks Circle to sign an expired message again (it expires 24 h after the attestation) */
export async function irisReattest(base: string, nonce: Hex, fetchFn: typeof fetch = fetch): Promise<void> {
  const res = await fetchFn(`${base}/v2/reattest/${nonce}`, { method: 'POST', signal: AbortSignal.timeout(10_000) });
  if (!res.ok) throw new Error(`Iris reattest HTTP ${res.status}`);
}

/** Circle's fee for a USDC burn between two domains, bps of the amount, per finality threshold */
export async function irisFees(
  base: string,
  sourceDomain: number,
  destinationDomain = HYPEREVM_DOMAIN,
  fetchFn: typeof fetch = fetch,
): Promise<{ fastBps: number; standardBps: number }> {
  const res = await fetchFn(`${base}/v2/burn/USDC/fees/${sourceDomain}/${destinationDomain}`, { signal: AbortSignal.timeout(10_000) });
  if (!res.ok) throw new Error(`Iris HTTP ${res.status}`);
  const rows = (await res.json()) as { finalityThreshold: number; minimumFee: number }[];
  const of = (threshold: number) => rows.find((r) => r.finalityThreshold === threshold)?.minimumFee;
  const fast = of(FINALITY.fast);
  const standard = of(FINALITY.standard);
  if (fast === undefined || standard === undefined) throw new Error('Iris fees: unexpected response');
  return { fastBps: fast, standardBps: standard };
}

export type BurnPick =
  | { kind: 'attested'; message: Hex; attestation: Hex; burn: CctpMessage }
  | { kind: 'waiting'; reason: string }
  | { kind: 'none' };

/**
 * The burn in a transaction's messages that the gateway can finalize: a V2 message to this domain minting to the
 * gateway, with its attestation. One still waiting for Circle is `waiting` (its recipient is unknown until then);
 * a transaction with no such burn is `none`.
 */
export function pickBurn(messages: IrisMessage[], gateway: Address, localDomain: number): BurnPick {
  let waiting: string | undefined;
  for (const m of messages) {
    if (m.cctpVersion !== 2) continue;
    if (!m.message || m.message === '0x') {
      waiting ??= m.delayReason ?? m.status;
      continue;
    }
    let burn: CctpMessage;
    try {
      burn = decodeCctpMessage(m.message);
    } catch {
      continue;
    }
    if (burn.destinationDomain !== localDomain) continue;
    if (wordToAddress(burn.mintRecipient)?.toLowerCase() !== gateway.toLowerCase()) continue;
    if (m.status !== 'complete' || !m.attestation || m.attestation === 'PENDING') {
      waiting ??= m.delayReason ?? m.status;
      continue;
    }
    return { kind: 'attested', message: m.message, attestation: m.attestation, burn };
  }
  return waiting ? { kind: 'waiting', reason: waiting } : { kind: 'none' };
}
